import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, MenuItem, Select, FormControl, InputLabel, Chip, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
} from '@mui/material';
import { BuildCircle as BuildCircleIcon } from '@mui/icons-material';
import { useRectifications, useUpdateRectification } from '../../../hooks/useForm16';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../../common/tableStyles';
import { SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
const CANCEL_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' };

// Soft tinted status pills.
const STATUS_CHIP = {
  OPEN: { bg: '#fffbeb', color: '#f59e0b' },
  IN_PROGRESS: { bg: '#eef4ff', color: '#2f6bff' },
  RESOLVED: { bg: '#f0fdf4', color: '#16a34a' },
  REJECTED: { bg: '#fef2f2', color: '#ef4444' },
};
const statusChipSx = (status) => {
  const s = STATUS_CHIP[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return { fontWeight: 700, bgcolor: s.bg, color: s.color, border: 'none' };
};

export default function RectificationsAdmin() {
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const params = useMemo(() => statusFilter ? { status: statusFilter } : {}, [statusFilter]);
  const { data: list = [], refetch } = useRectifications(params);
  const update = useUpdateRectification();
  const [picked, setPicked] = useState(null);
  const [resolution, setResolution] = useState('');

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BuildCircleIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Rectification Requests
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Review and resolve Form 16 rectification tickets raised by employees.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filters card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <FormControl size="small" sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="OPEN">Open</MenuItem>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
            <MenuItem value="RESOLVED">Resolved</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Table card */}
      <Paper elevation={0} sx={CARD_SX}>
        <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_PAPER_SX, boxShadow: 'none', border: 'none' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>Created</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Employee</TableCell>
                <TableCell sx={HEAD_CELL_SX}>FY</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Issue</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Description</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                <TableCell sx={HEAD_CELL_SX} />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8, border: 'none' }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BuildCircleIcon />
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No tickets found.</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : list.map((r) => (
                <TableRow key={r.id} sx={TABLE_ROW_SX} hover>
                  <TableCell sx={BODY_CELL_SX}>{r.created_at?.slice(0, 10)}</TableCell>
                  <TableCell sx={BODY_CELL_SX}>{r.employee_name} ({r.employee_no})</TableCell>
                  <TableCell sx={BODY_CELL_SX}>{r.fy_label}</TableCell>
                  <TableCell sx={BODY_CELL_SX}>{r.issue_section}</TableCell>
                  <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 320 }}>
                    <Typography variant="body2" noWrap>{r.description}</Typography>
                  </TableCell>
                  <TableCell sx={BODY_CELL_SX}><Chip size="small" label={r.status} sx={statusChipSx(r.status)} /></TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={() => { setPicked(r); setResolution(r.resolution_notes || ''); }}
                      sx={GRADIENT_BTN}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!picked} onClose={() => setPicked(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '22px' } }}>
        {picked && (
          <>
            <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              Ticket #{picked.id} — {picked.issue_section}
              <Chip size="small" label={picked.status} sx={statusChipSx(picked.status)} />
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>{picked.employee_name} · {picked.employee_no} · FY {picked.fy_label}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{picked.description}</Typography>
                </Grid>
                {picked.attachment && (
                  <Grid item xs={12}>
                    <a href={picked.attachment} target="_blank" rel="noreferrer">View attachment</a>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Resolution notes"
                    value={resolution} onChange={(e) => setResolution(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }} />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setPicked(null)} sx={CANCEL_BTN}>Close</Button>
              <Button
                variant="outlined"
                onClick={() => update.mutate({ id: picked.id, status: 'IN_PROGRESS', resolution_notes: resolution }, {
                  onSuccess: () => { setPicked(null); refetch(); },
                })}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: ACCENT, borderColor: `${ACCENT}55` }}
              >
                Mark In Progress
              </Button>
              <Button
                variant="contained"
                color="error"
                disableElevation
                onClick={() => update.mutate({ id: picked.id, status: 'REJECTED', resolution_notes: resolution }, {
                  onSuccess: () => { setPicked(null); refetch(); },
                })}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                disableElevation
                onClick={() => update.mutate({ id: picked.id, status: 'RESOLVED', resolution_notes: resolution }, {
                  onSuccess: () => { setPicked(null); refetch(); },
                })}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}
              >
                Resolve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
